/**
 * serviceWorkerRegistration.ts - PWA service worker registration for CRA.
 *
 * This module registers the service worker generated by Workbox (via CRA's
 * react-scripts build). The service worker caches static assets and enables
 * offline access to the application shell.
 *
 * Key behaviors:
 *	 - Only registers in production builds (process.env.NODE_ENV === 'production')
 *	 - Validates that the SW file is served from the same origin
 *	 - Provides onUpdate and onSuccess callbacks for the caller to react
 *	   to SW lifecycle events (e.g., showing a "new version available" banner)
 *	 - In development, checks whether the SW file actually exists and
 *	   unregisters stale workers from previous builds
 *
 * Based on the CRA PWA template with inline comments added.
 */

// Localhost patterns: used to detect if we are running on a local dev server
const isLocalhost = Boolean(
	window.location.hostname === 'localhost' ||
	// IPv4 loopback
	window.location.hostname === '[::1]' ||
	// IPv6 loopback
	window.location.hostname.match(
		/^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/
	)
);

/**
 * Configuration callbacks the caller can pass to register().
 *
 * @property onSuccess - Fired when the SW has been installed for the first time
 *						 and all assets are cached (ready for offline use).
 * @property onUpdate  - Fired when a new SW is available and waiting to activate.
 *						 The caller can prompt the user to refresh.
 */
type Config = {
	onSuccess?: (registration: ServiceWorkerRegistration) => void;
	onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

/**
 * register() - Entry point to register the production service worker.
 *
 * Does nothing if:
 *	 - The environment is not production
 *	 - The browser does not support service workers
 *	 - The page is not served from the same origin as the SW
 *
 * @param config - Optional callbacks for onSuccess / onUpdate events
 */
export function register(config?: Config): void {
	// Only register in production; dev uses hot reload instead
	if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
		// The public URL must be on the same origin for the SW to work
		const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
		if (publicUrl.origin !== window.location.origin) {
			// SW won't work if PUBLIC_URL is on a different origin (e.g., a CDN)
			return;
		}

		// Wait for the page to fully load before registering the SW
		// to avoid competing with critical resource requests
		window.addEventListener('load', () => {
			// CRA puts the compiled SW at the root of the public folder
			const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

			if (isLocalhost) {
				// On localhost, verify the SW file exists before registering
				checkValidServiceWorker(swUrl, config);

				// Log a helpful message for developers
				navigator.serviceWorker.ready.then(() => {
					console.log(
						'This web app is being served cache-first by a service worker. ' +
						'See https://cra.link/PWA'
					);
				});
			} else {
				// On a real domain, register the SW normally
				registerValidSW(swUrl, config);
			}
		});
	}
}

/**
 * registerValidSW() - Performs the actual SW registration and hooks up
 * lifecycle event listeners for install and update detection.
 *
 * @param swUrl	 - Full URL to the service-worker.js file
 * @param config - Optional callbacks for onSuccess / onUpdate
 */
function registerValidSW(swUrl: string, config?: Config): void {
	navigator.serviceWorker
		.register(swUrl)
		.then((registration) => {
			// Listen for new SW versions becoming available
			registration.onupdatefound = () => {
				const installingWorker = registration.installing;
				if (installingWorker == null) {
					return;
				}

				installingWorker.onstatechange = () => {
					if (installingWorker.state === 'installed') {
						if (navigator.serviceWorker.controller) {
							// A new SW is installed but waiting to activate.
							// This means new content is available but will only
							// be used after all tabs are closed.
							console.log(
								'New content is available and will be used when all ' +
								'tabs for this page are closed. See https://cra.link/PWA.'
							);

							// Notify the caller so they can show an update banner
							if (config && config.onUpdate) {
								config.onUpdate(registration);
							}
						} else {
							// First time install — all assets are now cached
							console.log('Content is cached for offline use.');

							// Notify the caller that the app is ready for offline
							if (config && config.onSuccess) {
								config.onSuccess(registration);
							}
						}
					}
				};
			};
		})
		.catch((error) => {
			console.error('Error during service worker registration:', error);
		});
}

/**
 * checkValidServiceWorker() - Validates that the SW URL actually points to
 * a real service worker file. If it returns a 404 or a non-JS content type,
 * we unregister any existing SW and reload the page.
 *
 * This prevents stale SWs from previous builds from running on localhost.
 *
 * @param swUrl	 - Full URL to the service-worker.js file
 * @param config - Optional callbacks for onSuccess / onUpdate
 */
function checkValidServiceWorker(swUrl: string, config?: Config): void {
	fetch(swUrl, {
		headers: { 'Service-Worker': 'script' },
	})
		.then((response) => {
			// Ensure the SW file exists and is actually JavaScript
			const contentType = response.headers.get('content-type');
			if (
				response.status === 404 ||
				(contentType != null && contentType.indexOf('javascript') === -1)
			) {
				// No valid SW found — unregister any existing one and reload
				navigator.serviceWorker.ready.then((registration) => {
					registration.unregister().then(() => {
						window.location.reload();
					});
				});
			} else {
				// SW file is valid — proceed with normal registration
				registerValidSW(swUrl, config);
			}
		})
		.catch(() => {
			console.log(
				'No internet connection found. App is running in offline mode.'
			);
		});
}

/**
 * unregister() - Removes any active service worker registration.
 *
 * Useful when transitioning away from PWA mode or for debugging.
 */
export function unregister(): void {
	if ('serviceWorker' in navigator) {
		navigator.serviceWorker.ready
			.then((registration) => {
				registration.unregister();
			})
			.catch((error) => {
				console.error(error.message);
			});
	}
}
